import { AuthQueryProvider } from "@daveyplate/better-auth-tanstack";
import { AuthUIProviderTanstack } from "@daveyplate/better-auth-ui/tanstack";
import { useEntitlement } from "@planvera/app-commons/subscription";
import type { QueryClient as ReactQueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	Outlet,
	useRouterState,
} from "@tanstack/react-router";
import { ThemeProvider } from "next-themes";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Toaster } from "sonner";
import { authClient } from "@/lib/auth-client";
import type { orpc } from "@/utils/orpc";
import { orpc as orpcUtils } from "@/utils/orpc";

/**
 * The VITE_APP_ID env var (e.g. "tool-labelling-license") is used to automatically
 * check that the user's organization has the required entitlement.
 */
const APP_ID = import.meta.env.VITE_APP_ID;

export interface RouterAppContext {
	orpc: typeof orpc;
	queryClient: ReactQueryClient;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
	component: RootComponent,
});

function RootComponent() {
	const { t } = useTranslation();
	const { data: session, isPending: isSessionLoading } =
		authClient.useSession();

	if (isSessionLoading) {
		return (
			<div className="flex h-screen items-center justify-center">
				<p className="text-muted-foreground">{t("common.loading")}</p>
			</div>
		);
	}

	if (!session?.user) {
		const webUrl = import.meta.env.VITE_WEB_URL || "https://localhost:3000";
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="text-center">
					<h1 className="font-bold text-2xl">{t("auth.signInRequired")}</h1>
					<p className="mt-2 text-muted-foreground">{t("auth.signInHint")}</p>
					<a
						href={`${webUrl}/auth/sign-in`}
						className="mt-4 inline-block text-primary hover:underline"
					>
						{t("auth.goToSignIn")}
					</a>
				</div>
			</div>
		);
	}

	return (
		<AuthQueryProvider>
			<AuthUIProviderTanstack authClient={authClient}>
				<ThemeProvider
					attribute="class"
					defaultTheme="light"
					disableTransitionOnChange
					storageKey="labelling-tool-theme"
				>
					<SubscriptionGateWrapper>
						<Outlet />
					</SubscriptionGateWrapper>
					<Toaster />
				</ThemeProvider>
			</AuthUIProviderTanstack>
		</AuthQueryProvider>
	);
}

function SubscriptionGateWrapper({ children }: { children: ReactNode }) {
	const { t } = useTranslation();
	// PLA-302: org id is resolved server-side from the project. Pass projectId
	// from URL search-param; entitlement procedure forbids client-supplied orgId.
	const projectId = useRouterState({
		select: (s) => (s.location.search as { projectId?: string }).projectId,
	});

	const {
		isAllowed,
		isLoading: isCheckingAccess,
		reason,
	} = useEntitlement({
		queryOptions: orpcUtils.entitlements.checkProjectFeature.queryOptions({
			input: {
				projectId: projectId ?? "",
				featureName: APP_ID ?? "",
			},
		}),
		enabled: !!projectId && !!APP_ID,
	});

	if (!APP_ID) {
		// No APP_ID configured — skip entitlement check
		return <>{children}</>;
	}

	// No project in URL (landing/wizard): render children; per-project gate
	// activates once user picks a project.
	if (!projectId) {
		return <>{children}</>;
	}

	if (isCheckingAccess) {
		return (
			<div className="flex h-screen items-center justify-center">
				<p className="text-muted-foreground">
					{t("auth.checkingSubscription")}
				</p>
			</div>
		);
	}

	if (!isAllowed) {
		if (reason === "USER_LICENSE_REQUIRED") {
			return (
				<div className="flex h-screen items-center justify-center">
					<div className="text-center">
						<h1 className="font-bold text-2xl">{t("auth.licenseRequired")}</h1>
						<p className="mt-2 text-muted-foreground">
							{t("auth.licenseRequiredHint")}
						</p>
					</div>
				</div>
			);
		}

		return (
			<div className="flex h-screen items-center justify-center">
				<div className="text-center">
					<h1 className="font-bold text-2xl">{t("auth.upgradeRequired")}</h1>
					<p className="mt-2 text-muted-foreground">{t("auth.upgradeHint")}</p>
				</div>
			</div>
		);
	}

	return <>{children}</>;
}
